// src/hooks/useFilingsData.js
// SEC-FILINGS-PAGE — new file.
//
// Shared by FilingsPage.jsx (standalone, full category+feed browsing) AND
// StockPage.jsx's Filings tab (ticker-scoped, feed only, no category grid).
// One fetch/pagination implementation — the two surfaces must never grow
// separate copies of this logic.
//
// TWO DIFFERENT NOTIONS OF "ticker" — do not merge them:
//   fixedTicker — passed by StockPage.jsx's tab. The page IS that ticker;
//                 there is no search box, it never changes mid-session.
//   filters.ticker — set by the standalone page's search input. A filter
//                 the member/guest can change and clear.
//   effectiveTicker = fixedTicker || filters.ticker — fixedTicker always
//                 wins when both a tab context and a filter exist, since
//                 a tab should never be overridden by leftover filter state.
//
// Auth: sends Authorization header when a token is present — the backend's
// isAuthenticatedRequest() is what actually determines gated capabilities
// server-side; this hook does not attempt to enforce anything client-side,
// it only supplies the header and exposes isLoggedIn for UI affordance.

import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function useFilingsData({ token, fixedTicker, includeCategories = true } = {}) {
  const isLoggedIn = !!token;
  const authHeaders = token ? { Authorization: 'Bearer ' + token } : {};

  const [categoryOverview, setCategoryOverview] = useState(null);
  const [categoriesLoading, setCategoriesLoading] = useState(includeCategories);
  const [categoriesError, setCategoriesError] = useState('');

  const [filters, setFiltersState] = useState({
    category: null,
    formType: null,
    ticker: null,      // standalone page's search box only — see header note
    sort: 'filedAt',   // 'filedAt' | 'significance' — significance is member-only, enforced server-side
    preset: null,      // 'high_conviction' | null — member-only, enforced server-side
    page: 1,
  });

  const [filings, setFilings]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const setFilters = useCallback((patch) => {
    setFiltersState(prev => ({
      ...prev,
      ...patch,
      // Any filter change other than page itself resets to page 1 —
      // avoids landing on an empty page 4 of a newly-narrowed result set.
      page: Object.prototype.hasOwnProperty.call(patch, 'page') ? patch.page : 1,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState({ category: null, formType: null, ticker: null, sort: 'filedAt', preset: null, page: 1 });
  }, []);

  const effectiveTicker = fixedTicker || filters.ticker;

  // ── Category overview — fetched once, independent of feed filters ────────
  // Never fetched at all in tab mode (includeCategories=false) — the tab
  // has no Category Grid to feed.
  useEffect(() => {
    if (!includeCategories) { setCategoriesLoading(false); return; }
    let cancelled = false;
    setCategoriesLoading(true);
    fetch(API_URL + '/api/filings/categories')
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.success) { setCategoryOverview(d); setCategoriesError(''); }
        else setCategoriesError('Category data unavailable');
      })
      .catch(() => { if (!cancelled) setCategoriesError('Category data unavailable'); })
      .finally(() => { if (!cancelled) setCategoriesLoading(false); });
    return () => { cancelled = true; };
  }, [includeCategories]);

  // ── Feed — refetches on filter change, ticker change, or auth change ─────
  // (auth change matters: signing in mid-session should re-run the same
  // query with capabilities now unlocked server-side)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    const params = new URLSearchParams();
    if (filters.category)   params.set('category', filters.category);
    if (filters.formType)   params.set('formType', filters.formType);
    if (filters.sort)       params.set('sort', filters.sort);
    if (filters.preset)     params.set('preset', filters.preset);
    if (effectiveTicker)    params.set('ticker', effectiveTicker);
    params.set('page', String(filters.page));

    fetch(API_URL + '/api/filings?' + params.toString(), { headers: authHeaders })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.success) {
          setFilings(d.filings || []);
          setTotal(d.total || 0);
          setPageSize(d.pageSize || 25);
        } else {
          setError('Filings unavailable');
        }
      })
      .catch(() => { if (!cancelled) setError('Filings unavailable'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category, filters.formType, filters.ticker, filters.sort, filters.preset, filters.page, fixedTicker, token]);

  return {
    isLoggedIn,
    categoryOverview, categoriesLoading, categoriesError,
    filters, setFilters, resetFilters,
    filings, total, pageSize, loading, error,
  };
}
